import React, { useState, useEffect } from 'react';
import {
    X,
    User,
    Users,
    Clock,
    CheckCircle2,
    AlertTriangle,
    Download,
    Send,
    BarChart3,
    TrendingUp,
    Calendar,
    Award,
    Timer
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const ComplianceTaskStatsModal = ({ isOpen, onClose, taskId, taskTitle, onSendReminder }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('employees'); // 'employees', 'roles'
    const [selectedEmployees, setSelectedEmployees] = useState([]);

    useEffect(() => {
        if (isOpen && taskId) {
            fetchStats();
        }
    }, [isOpen, taskId]);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/compliance/tasks/${taskId}/stats`);
            setStats(response.data.stats);
        } catch (error) {
            console.error('Error fetching task stats:', error);
            toast.error('Failed to load task statistics');
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        const loadingToast = toast.loading('Exporting data...');
        try {
            const response = await api.get(`/compliance/tasks/${taskId}/export`);
            
            // Create CSV
            const exportData = response.data.export;
            let csv = 'First Name,Last Name,Email,Position,Department,Status,Assigned Date,Due Date,Completed Date,Days Overdue\n';
            
            exportData.employees.forEach(emp => {
                csv += `"${emp.first_name}","${emp.last_name}","${emp.email}","${emp.position || ''}","${emp.department || ''}","${emp.status}","${emp.assigned_date || ''}","${emp.due_date || ''}","${emp.completed_at || ''}","${emp.days_overdue || 0}"\n`;
            });

            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${taskTitle.replace(/[^a-z0-9]/gi, '_')}_compliance_data.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            toast.success('Data exported successfully!', { id: loadingToast });
        } catch (error) {
            console.error('Error exporting data:', error);
            toast.error('Failed to export data', { id: loadingToast });
        }
    };

    const handleSelectEmployee = (employeeId) => {
        setSelectedEmployees(prev => 
            prev.includes(employeeId) 
                ? prev.filter(id => id !== employeeId)
                : [...prev, employeeId]
        );
    };

    const handleSelectAllIncomplete = () => {
        const incompleteIds = stats.employees
            .filter(emp => emp.status !== 'completed')
            .map(emp => emp.employee_id);
        setSelectedEmployees(incompleteIds);
    };

    const handleSendReminder = () => {
        if (selectedEmployees.length === 0) {
            toast.error('Please select at least one employee');
            return;
        }
        onSendReminder(selectedEmployees);
    };

    const getStatusBadge = (status) => {
        const styles = {
            completed: 'bg-green-100 text-green-700',
            in_progress: 'bg-blue-100 text-blue-700',
            pending: 'bg-gray-100 text-gray-700',
            pending_review: 'bg-yellow-100 text-yellow-700'
        };
        return styles[status] || styles.pending;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <BarChart3 className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">Task Stats: {taskTitle}</h2>
                            <p className="text-sm text-gray-500">Compliance task statistics and employee breakdown</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                {loading ? (
                    <div className="flex-1 flex items-center justify-center p-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    </div>
                ) : stats ? (
                    <div className="flex-1 overflow-y-auto p-6">
                        {/* Overall Statistics */}
                        <div className="mb-6">
                            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-3">
                                Overall Statistics
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                                        <Users className="w-4 h-4" />
                                        <span className="text-xs font-medium">Total Assigned</span>
                                    </div>
                                    <p className="text-2xl font-bold text-gray-800">{stats.overview.total_assigned}</p>
                                </div>
                                <div className="bg-green-50 rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-green-600 mb-1">
                                        <CheckCircle2 className="w-4 h-4" />
                                        <span className="text-xs font-medium">Completed</span>
                                    </div>
                                    <p className="text-2xl font-bold text-green-600">{stats.overview.completed}</p>
                                    <p className="text-xs text-green-600">{stats.overview.completion_rate}%</p>
                                </div>
                                <div className="bg-yellow-50 rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-yellow-600 mb-1">
                                        <Clock className="w-4 h-4" />
                                        <span className="text-xs font-medium">Incomplete</span>
                                    </div>
                                    <p className="text-2xl font-bold text-yellow-600">{stats.overview.incomplete}</p>
                                </div>
                                <div className="bg-red-50 rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-red-600 mb-1">
                                        <AlertTriangle className="w-4 h-4" />
                                        <span className="text-xs font-medium">Overdue</span>
                                    </div>
                                    <p className="text-2xl font-bold text-red-600">{stats.overview.overdue}</p>
                                </div>
                                <div className="bg-blue-50 rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-blue-600 mb-1">
                                        <Timer className="w-4 h-4" />
                                        <span className="text-xs font-medium">Avg. Time</span>
                                    </div>
                                    <p className="text-2xl font-bold text-blue-600">
                                        {stats.overview.avg_completion_days || '-'}
                                    </p>
                                    <p className="text-xs text-blue-600">days</p>
                                </div>
                            </div>
                        </div>

                        {/* Company-Wide Metrics */}
                        {(stats.highlights.first_completer || stats.highlights.fastest_completer) && (
                            <div className="mb-6">
                                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-3">
                                    Company-Wide Metrics
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {stats.highlights.first_completer && (
                                        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg p-4 border border-amber-200">
                                            <div className="flex items-center gap-2 text-amber-700 mb-2">
                                                <Award className="w-4 h-4" />
                                                <span className="text-xs font-semibold">First Completion</span>
                                            </div>
                                            <p className="font-medium text-gray-800">
                                                {stats.highlights.first_completer.first_name} {stats.highlights.first_completer.last_name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {formatDate(stats.highlights.first_completer.completed_at)}
                                            </p>
                                        </div>
                                    )}
                                    {stats.highlights.fastest_completer && (
                                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                                            <div className="flex items-center gap-2 text-green-700 mb-2">
                                                <TrendingUp className="w-4 h-4" />
                                                <span className="text-xs font-semibold">Fastest Completion</span>
                                            </div>
                                            <p className="font-medium text-gray-800">
                                                {stats.highlights.fastest_completer.first_name} {stats.highlights.fastest_completer.last_name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {parseFloat(stats.highlights.fastest_completer.completion_days).toFixed(1)} days
                                            </p>
                                        </div>
                                    )}
                                    {stats.overview.compliance_impact > 0 && (
                                        <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg p-4 border border-red-200">
                                            <div className="flex items-center gap-2 text-red-700 mb-2">
                                                <AlertTriangle className="w-4 h-4" />
                                                <span className="text-xs font-semibold">Compliance Impact</span>
                                            </div>
                                            <p className="font-medium text-gray-800">
                                                {stats.overview.compliance_impact} violation{stats.overview.compliance_impact !== 1 ? 's' : ''}
                                            </p>
                                            <p className="text-xs text-gray-500">from this task</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Tabs */}
                        <div className="border-b border-gray-200 mb-4">
                            <nav className="-mb-px flex space-x-6">
                                <button
                                    onClick={() => setActiveTab('employees')}
                                    className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                                        activeTab === 'employees'
                                            ? 'border-primary text-primary'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Per Employee Breakdown
                                </button>
                                <button
                                    onClick={() => setActiveTab('roles')}
                                    className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                                        activeTab === 'roles'
                                            ? 'border-primary text-primary'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Per Role Breakdown
                                </button>
                            </nav>
                        </div>

                        {/* Employee Breakdown */}
                        {activeTab === 'employees' && (
                            <div>
                                {stats.overview.incomplete > 0 && (
                                    <div className="flex items-center justify-between mb-3">
                                        <button
                                            onClick={handleSelectAllIncomplete}
                                            className="text-sm text-primary hover:text-primary-hover font-medium"
                                        >
                                            Select all incomplete ({stats.overview.incomplete})
                                        </button>
                                        {selectedEmployees.length > 0 && (
                                            <span className="text-sm text-gray-500">
                                                {selectedEmployees.length} selected
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                {stats.overview.incomplete > 0 && (
                                                    <th className="px-4 py-3 w-10"></th>
                                                )}
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                                    Employee
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                                    Role
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                                    Status
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                                    Completed
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                                    Overdue
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {stats.employees.map((emp) => (
                                                <tr key={emp.employee_id} className="hover:bg-gray-50">
                                                    {stats.overview.incomplete > 0 && (
                                                        <td className="px-4 py-3">
                                                            {emp.status !== 'completed' && (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedEmployees.includes(emp.employee_id)}
                                                                    onChange={() => handleSelectEmployee(emp.employee_id)}
                                                                    className="w-4 h-4 text-primary rounded focus:ring-primary"
                                                                />
                                                            )}
                                                        </td>
                                                    )}
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                                                <User className="w-4 h-4 text-gray-500" />
                                                            </div>
                                                            <div>
                                                                <p className="font-medium text-gray-800 text-sm">
                                                                    {emp.first_name} {emp.last_name}
                                                                </p>
                                                                <p className="text-xs text-gray-500">{emp.email}</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="text-sm text-gray-600">{emp.position || '-'}</span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(emp.status)}`}>
                                                            {emp.status.replace('_', ' ')}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="text-sm text-gray-600">
                                                            {emp.completed_at ? formatDate(emp.completed_at) : '-'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {emp.days_overdue > 0 ? (
                                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                                                <Clock className="w-3 h-3" />
                                                                {Math.round(emp.days_overdue)} days
                                                            </span>
                                                        ) : (
                                                            <span className="text-sm text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Role Breakdown */}
                        {activeTab === 'roles' && (
                            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                                Role
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                                Assigned
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                                Completed
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                                Rate
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {stats.roles.map((role, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50">
                                                <td className="px-4 py-3">
                                                    <span className="font-medium text-gray-800">{role.role}</span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="text-gray-600">{role.assigned}</span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="text-gray-600">{role.completed}</span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 h-2 bg-gray-100 rounded-full max-w-[100px]">
                                                            <div
                                                                className={`h-full rounded-full ${
                                                                    role.completion_rate >= 100 
                                                                        ? 'bg-green-500' 
                                                                        : role.completion_rate >= 90 
                                                                            ? 'bg-yellow-500' 
                                                                            : 'bg-red-500'
                                                                }`}
                                                                style={{ width: `${role.completion_rate}%` }}
                                                            />
                                                        </div>
                                                        <span className={`text-sm font-medium ${
                                                            role.completion_rate >= 100 
                                                                ? 'text-green-600' 
                                                                : role.completion_rate >= 90 
                                                                    ? 'text-yellow-600' 
                                                                    : 'text-red-600'
                                                        }`}>
                                                            {role.completion_rate}%
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex-1 flex items-center justify-center p-12">
                        <p className="text-gray-500">No data available</p>
                    </div>
                )}

                {/* Footer */}
                <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
                    <button
                        onClick={handleExport}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        <Download className="w-4 h-4" />
                        Export This Task Data
                    </button>
                    {selectedEmployees.length > 0 && (
                        <button
                            onClick={handleSendReminder}
                            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium transition-colors"
                        >
                            <Send className="w-4 h-4" />
                            Send Reminder to {selectedEmployees.length} Employee{selectedEmployees.length !== 1 ? 's' : ''}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ComplianceTaskStatsModal;

